import { Alert, AlertTitle } from "@mui/material";

/**
 * Estado de erro honesto das telas de atividade que leem o contrato ao abrir.
 *
 * why: desde a v0.0.167 as rotas de `/missing-data` devolvem 404 a quem não é
 * parte do workspace. A tela de dados faltantes do buyer fazia
 * `if (!data) return <div>Loading</div>` e ficava em "Loading" para sempre; a do
 * seller ficava em branco (o "Error..." só aparecia antes do primeiro fetch
 * terminar); a de campanhas mostrava "Error..." sem dizer o quê.
 */
export const SEM_ACESSO_AO_CONTRATO = "Você não tem acesso a este contrato";
export const NAO_FOI_POSSIVEL_CARREGAR = "Não foi possível carregar";
export const ERRO_INTERNO_DO_SERVIDOR = "O servidor teve um erro interno. Tente de novo em alguns minutos.";

/**
 * why: os controllers de `/missing-data` respondem 500 com
 * `{ error: "Internal server error", details }`. O texto em inglês não diz nada
 * ao usuário e o `details` é a mensagem técnica do Snowflake; nenhum dos dois
 * vai para a tela.
 */
const GENERICAS = /^internal server error\.?$/i;

interface IErroHttp {
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
}

export function statusDoErro(error: unknown): number | undefined {
  return (error as IErroHttp | null | undefined)?.response?.status;
}

/** 403 e 404: o backend recusa sem confirmar se o contrato existe. */
export function semAcessoAoContrato(error: unknown): boolean {
  const status = statusDoErro(error);
  return status === 403 || status === 404;
}

/**
 * Mensagem que o backend mandou, nos formatos que ele usa: `{ message }` (erros
 * HTTP do helper), `{ data: { message } }` e `{ error }` (alguns controllers).
 */
export function mensagemDoBackend(error: unknown): string | undefined {
  const corpo = (error as IErroHttp | null | undefined)?.response?.data as
    | { message?: unknown; error?: unknown; data?: { message?: unknown } }
    | string
    | undefined;
  if (!corpo) return undefined;
  if (typeof corpo === "string") return corpo.trim() || undefined;
  for (const candidato of [corpo.message, corpo.data?.message, corpo.error]) {
    if (typeof candidato !== "string" || !candidato.trim()) continue;
    return GENERICAS.test(candidato.trim()) ? ERRO_INTERNO_DO_SERVIDOR : candidato.trim();
  }
  return undefined;
}

/** Texto do erro para a tela: nunca "Loading", nunca só "Error...". */
export function textoDoErroAoCarregar(error: unknown): { titulo: string; detalhe?: string } {
  if (semAcessoAoContrato(error)) return { titulo: SEM_ACESSO_AO_CONTRATO };
  const status = statusDoErro(error);
  const detalhe =
    mensagemDoBackend(error) ?? (status ? `O servidor respondeu com erro ${status}.` : "O servidor não respondeu.");
  return { titulo: NAO_FOI_POSSIVEL_CARREGAR, detalhe };
}

/**
 * Intervalo de polling que para depois de uma recusa de acesso: repetir a cada
 * 5 segundos um 403 ou 404 não muda a resposta e só enche o log do backend.
 */
export function intervaloQueParaSemAcesso(intervaloMs: number) {
  return (query: { state: { error: unknown } }): number | false =>
    semAcessoAoContrato(query.state.error) ? false : intervaloMs;
}

/**
 * Política de nova tentativa do react-query: recusa de acesso não muda ao
 * repetir, então não se repete; os outros erros mantêm as 3 tentativas padrão.
 */
export function tentarDeNovoAoCarregar(failureCount: number, error: unknown): boolean {
  if (semAcessoAoContrato(error) || statusDoErro(error) === 401) return false;
  return failureCount < 3;
}

export function ErroAoCarregar({ error }: { error: unknown }) {
  const { titulo, detalhe } = textoDoErroAoCarregar(error);
  return (
    <Alert severity={semAcessoAoContrato(error) ? "warning" : "error"} sx={{ m: 2 }}>
      <AlertTitle>{titulo}</AlertTitle>
      {detalhe}
    </Alert>
  );
}
